import React, { useContext, useMemo, useState } from 'react';

import { dashboardArrivalNoticeHooks } from '@apis/next/arrivalNoticeApis';
import { dashboardBookingInquiryApis } from '@apis/next/bookingInquiryApis';
import { dashboardCustomsInstructionApis } from '@apis/next/customsInstructionApis';
import { dashboardCustomsRequestApis } from '@apis/next/customsRequestApis';
import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { BookingInquiryOwnerChooseDialog } from '@components/admin/booking/BookingInquiryOwnerChooseDialog';
import { ChangeAssigneeDialog } from '@components/admin/booking/ChangeAssigneeDialog';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { TicketBubble } from '@components/admin/booking/TicketBubble';
import { QueryContainer } from '@components/QueryContainer';
import { SpinnerModal } from '@components/SpinnerModal';
import { CancelBookingDialog } from '@components/utilities/Bookings/CancelBookingDialog';
import { VerticalSpace } from '@components/VerticalSpace';
import { arrivalNoticeStrings, deliveryDestinationOptions } from '@data/arrivalNotice';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { bookingInquiryProfitStrings } from '@data/bookingInquiryProfit';
import { bookingsStrings } from '@data/bookings';
import { cargoTrackingStrings } from '@data/cargoTracking';
import { contactStrings } from '@data/contacts';
import { customsRequestStrings } from '@data/customsRequest';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { instructionStrings } from '@data/instruction';
import { invoiceStrings } from '@data/invoice';
import { moneyStrings } from '@data/money';
import { paymentSlipStrings } from '@data/paymentSlip';
import { preAlertStrings } from '@data/preAlert';
import { routePlanServiceNameStrings, routePlanStrings } from '@data/routePlan';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { usePatchBookingInquiryOwner } from '@hooks/mutation/dashboard/usePatchBookingInquiryOwner';
import { usePatchBookingInquiryStatus } from '@hooks/mutation/dashboard/usePatchBookingInquiryStatus';
import { usePatchTrackingData } from '@hooks/mutation/dashboard/usePatchTrackingData';
import { usePostBookingInquirySalespersonChina } from '@hooks/mutation/dashboard/usePostBookingInquirySalespersonChina';
import { usePostBookingInquirySalespersonJapan } from '@hooks/mutation/dashboard/usePostBookingInquirySalespersonJapan';
import { useCompanyDetail } from '@hooks/queries/dashboard/useCompanyDetail';
import { useDeliveryInstruction } from '@hooks/queries/dashboard/useDeliveryInstruction';
import { useUserList } from '@hooks/queries/dashboard/useUserList';
import { useZendeskD2dTickets } from '@hooks/queries/dashboard/useZendeskD2dTickets';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import { allCompanyLevel } from '@models/Company';
import { type Country } from '@models/Country';
import { BookingInquiryProfitHelper } from '@models/next/BookingInquiryProfitHelper';
import { CustomsStatusName } from '@models/next/CustomsRequestStatus';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDate, includes, mapOptional } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';

const strings = createStrings({
  ja: {
    d2dNo: 'D2D番号',
    createdAt: '受付時間',
    applicant: '申込者',
    owner: 'Owner',
    csManagerCN: 'CS担当者（中国）',
    csManagerJP: 'CS担当者（日本）',
    cancel: 'キャンセル',
    pol: 'POL',
    pod: 'POD',
    polPod: 'POL/POD',
    consignee: 'CNEE',
    etd: 'ETD',
    eta: 'ETA',
    nums: '個数',
    becomeAssignee: '担当者になる',
    job: '台帳番号',
    commodity: 'Commodity',
    quantity_packages: '荷姿',
    measurement: 'Measurement',
    grossWeight: 'Gross Weight',
    followers: 'Followers',
    routePlanName: '航路表示',
    customsSpecialNote: '通関に関する特記事項',
    basicInfo: '基本情報',
    deliveryInfo: '配送情報',
    customsInfo: '通関情報',
    invoicePaymentInfo: '請求・支払い情報',
    pickup: '集荷',
    option: 'オプション',
    moveOutStatus: '出庫手配',
    moveOutDate: '引取日',
    customs: '通関',
    vessel: 'VESSEL',
    foodDelivery: '食品届',
    applicationRecord: '申込実績',
    deliveryStatus: '配送状況',
    commodityInfo: '貨物情報',
    truck: 'トラック',
    price: '料金',
    grade: 'グレード',
    toManagementTool: '案件管理ツールへ',
    inconsistentDeliveryDestination: '搬入先が不一致',
    notAuthorized: (csManagerByCountry: string) => `${csManagerByCountry}になる権限はありません。`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  managementInfoTable: {
    ...common.textAlignCenter,
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
    '& > div > div': {
      ...common.textGray700,
      padding: '0.75rem',
      border: `1px solid ${theme.palette.base[200]}`,
    },
    '& > div > div[role="head"]': {
      ...common.textGray800,
      ...common.fontWeight700,
      backgroundColor: theme.palette.base[200],
    },
  },
  bookingDetailsManagementInfoContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    '& > div': {
      ...common.textGray700,
      ...common.whitespacePre,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minHeight: '60px',
      padding: '0.5rem',
      borderLeft: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).toString()}`,
      borderBottom: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).toString()}`,
    },
    '& > div:nth-child(-n+4)': {
      borderTop: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).toString()}`,
    },
    '& > div:nth-child(4n)': {
      borderRight: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).toString()}`,
    },
    '& > div[role="head"]': {
      ...common.fontWeight700,
      ...common.textBlack,
      ...common.flexCenter,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
    '& > div[role="body"]': {
      ...common.wordBreakBreakAll,
      ...common.whitespacePreWrap,
      alignItems: 'center',
      display: 'grid',
      gridTemplateColumns: '1fr 2fr',
      columnGap: '1rem',
    },
  },
  managementInfoCellTitle: {
    ...common.fontWeight700,
    ...common.textBlack,
  },
  noWrapCell: {
    ...common.whitespacePre,
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    overflow: 'hidden',
  },
}));

export interface BookingDetailsManagementInfoProps {
  onScrollToManagementTool: () => void;
}

export const BookingDetailsManagementInfo: React.FC<BookingDetailsManagementInfoProps> = (
  props,
) => {
  const { onScrollToManagementTool } = props;

  const { bookingInquiry, preAlert, user } = useContext(BookingDetailsContext);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const consigneeCompany = useCompanyDetail(bookingInquiry.consignee?.user!, {
    enabled: !!bookingInquiry.consignee?.user,
  });
  const zendeskTickets = useZendeskD2dTickets(bookingInquiry.d2d_no);
  const companyStaffsQuery = useUserList(
    {
      company_name: bookingInquiry.company?.company_name,
      page_size: 100,
    },
    {
      enabled: !!bookingInquiry.company?.company_name,
    },
  );

  const customsInstruction = dashboardCustomsInstructionApis.useGet(
    '/dashboard/customs-instruction/:d2d_no',
    {
      params: {
        d2d_no: bookingInquiry.d2d_no,
      },
    },
  );

  const customsRequest = dashboardCustomsRequestApis.useGet(
    '/dashboard/customs-request/:d2d_no',
    {
      params: { d2d_no: bookingInquiry.d2d_no },
    },
    {
      enabled: !!customsInstruction.data?.status.find((s) => s.name === '確認済み'),
    },
  );

  const deliveryInstruction = useDeliveryInstruction(bookingInquiry.d2d_no);
  const deliveryRequest = dashboardDeliveryRequestApis.useGet(
    '/dashboard/delivery-request/:id',
    {
      params: { id: deliveryInstruction.data?.delivery_requests?.[0]?.id! },
    },
    {
      enabled: !!deliveryInstruction.data?.status.find((s) => s.name === '確認済み'),
    },
  );

  const arrivalNotice = dashboardArrivalNoticeHooks.useGet(
    '/dashboard/arrival-notice/:id',
    {
      params: { id: preAlert.arrival_notice_id! },
    },
    { enabled: !!preAlert.arrival_notice_id },
  );

  const bookingStatusFlows = dashboardBookingInquiryApis.useGet(
    '/dashboard/booking-inquiry/:d2d_no/status-flows',
    {
      params: {
        d2d_no: bookingInquiry.d2d_no,
      },
    },
  );

  const { profit } = preAlert;
  const bookingInquiryProfitHelper = new BookingInquiryProfitHelper(profit);

  const canEdit = new AdminInquiryDetailHelper(bookingInquiry).canEdit(user, preAlert);

  const canUserBecomeSalesperson = {
    cn: AdminInquiryDetailHelper.canUserBecomeSalespersonChina(user, preAlert),
    jp: AdminInquiryDetailHelper.canUserBecomeSalespersonJapan(user, preAlert),
  };

  const grossProfit = bookingInquiryProfitHelper.grossProfit;
  const sales = bookingInquiryProfitHelper.sales;

  const doStatuses = bookingStatusFlows.data?.do_status_flow.status_flows;
  const lastDoStatus = doStatuses?.[doStatuses.length - 1];

  const moveOutStatuses = bookingStatusFlows.data?.move_out_flow.status_flows;
  const lastMoveOutStatus = moveOutStatuses?.[moveOutStatuses.length - 1];

  const invoiceStatus = bookingStatusFlows.data?.invoice_status_flow.status_flows;
  const lastInvoiceStatus = invoiceStatus?.[invoiceStatus.length - 1];

  const paymentSlipStatus = bookingStatusFlows.data?.payment_slip_status_flow.status_flows;
  const lastPaymentSlipStatus = paymentSlipStatus?.[paymentSlipStatus.length - 1];

  const customsStatuses = customsRequest.data?.status;
  const lastCustomsStatus = customsStatuses?.[customsStatuses.length - 1];

  const deliveryStatuses = deliveryRequest.data?.status;
  const lastDeliveryStatus = deliveryStatuses?.[deliveryStatuses.length - 1];

  const hasCustomsCheck = customsStatuses?.find((s) =>
    includes([CustomsStatusName.enum.税関検査, CustomsStatusName.enum['本申告入り 区3']], s.name),
  );

  const relatedUserEmails = useMemo(() => {
    return uniq([
      ...bookingInquiry.can_views
        .filter((v) => !(v.can_view_type === 'creator' && bookingInquiry.source === 'admin')) // Hide the staff who creates the booking
        .map((p) => p.email),
      ...(zendeskTickets.data ?? []).map((p) => p.requester.email),
      ...(companyStaffsQuery.data?.results ?? []).map((p) => p.email),
    ]);
  }, [bookingInquiry, zendeskTickets.data, companyStaffsQuery.data]);

  const relatedUsers = useUserList(
    {
      emails: relatedUserEmails,
    },
    {
      enabled:
        zendeskTickets.isSuccess &&
        (!bookingInquiry.owner_company?.id || companyStaffsQuery.isSuccess),
    },
  );

  const [cancelBookingOpen, setCancelBookingOpen] = useState(false);
  const [changeSalespersonOpen, setChangeSalespersonOpen] = useState(false);
  const [salespersonCountry, setSalespersonCountry] = useState<Country>('cn');
  const [openOwnerChooseDialog, setOpenOwnerChooseDialog] = useState(false);

  const patchInquiryStatus = usePatchBookingInquiryStatus(bookingInquiry.d2d_no);
  const patchTrackingData = usePatchTrackingData(bookingInquiry.d2d_no);
  const postInquirySalespersonChina = usePostBookingInquirySalespersonChina(bookingInquiry.d2d_no);
  const postInquirySalespersonJapan = usePostBookingInquirySalespersonJapan(bookingInquiry.d2d_no);
  const patchBookingInquiryOwner = usePatchBookingInquiryOwner(bookingInquiry.d2d_no);

  const notAuthorizedAlert = useConfirmDialog();

  const userHelper = new UserHelper(user);
  const isAdmin = userHelper.isAdmin;

  const isJapanStaff = userHelper.isJapanStaff;

  const isCancelled =
    bookingInquiry.status && ['FCキャンセル済', '客側キャンセル済'].includes(bookingInquiry.status);

  const renderCancelCell = () => {
    const content = isCancelled ? (
      bookingInquiry.status
    ) : (
      <a
        className={clsx(
          classes.link,
          foldMessagesEither(
            canEdit,
            () => classes.linkDisabled,
            () => undefined,
          ),
        )}
        onClick={() =>
          foldMessagesEither(
            canEdit,
            ({ messages }) => {
              notAuthorizedAlert.confirm({ messages });
            },
            () => {
              setCancelBookingOpen(true);
            },
          )
        }
      >
        {strings.cancel}
      </a>
    );

    return <>{content}</>;
  };

  const renderCancelBookingDialog = () => {
    return (
      <CancelBookingDialog
        cancellable
        open={cancelBookingOpen}
        onClose={() => setCancelBookingOpen(false)}
        no={bookingInquiry.d2d_no}
        type={bookingInquiry?.status ?? '-'}
        onAction={async (yesOrNo) => {
          if (yesOrNo === 'NO') {
            return;
          }

          await Promise.all([
            patchInquiryStatus.mutateAsync({
              status: 'FCキャンセル済',
            }),
            patchTrackingData.mutateAsync({
              transport_status: cargoTrackingStrings.cancelled,
              place: '',
              time: new Date().toISOString(),
              coop_name: undefined,
              coop_tracking_no: undefined,
              car_driver: undefined,
              car_no: undefined,
              file: undefined,
              note: undefined,
              key: undefined,
            }),
          ]);
        }}
      />
    );
  };

  const renderSalesperson = (country: Country) => {
    const salesperson =
      country === 'cn' ? bookingInquiry.salesperson_china : bookingInquiry.salesperson_japan;
    const salespersonName = salesperson ? new UserHelper(salesperson).fullname : '-';

    const agencyNameEnShort =
      salesperson &&
      bookingInquiry.salesperson_china?.role === 'FCChina' &&
      bookingInquiry.route_plan?.agency.name_en_short;

    return (
      <>
        {country === 'cn' && agencyNameEnShort && `${agencyNameEnShort}-`}
        {salespersonName}
        <a
          style={{ marginLeft: '1rem' }}
          className={clsx(
            classes.link,
            foldMessagesEither(
              canUserBecomeSalesperson[country],
              () => classes.linkDisabled,
              () => undefined,
            ),
            salesperson?.email === user.email && classes.linkDisabled,
          )}
          onClick={() => {
            if (salesperson?.email !== user.email) {
              foldMessagesEither(
                canUserBecomeSalesperson[country],
                ({ messages }) => {
                  notAuthorizedAlert.confirm({
                    messages,
                  });
                },
                () => {
                  setSalespersonCountry(country);
                  setChangeSalespersonOpen(true);
                },
              );
            }
          }}
        >
          {strings.becomeAssignee}
        </a>
      </>
    );
  };

  const renderChangeSalesperson = () => {
    return (
      <ChangeAssigneeDialog
        open={changeSalespersonOpen}
        onCancel={() => setChangeSalespersonOpen(false)}
        onYes={async () => {
          salespersonCountry === 'cn'
            ? await postInquirySalespersonChina.mutateAsync()
            : await postInquirySalespersonJapan.mutateAsync();
          setChangeSalespersonOpen(false);
        }}
      />
    );
  };

  const renderTicketBubble = (email: string) => {
    const user = relatedUsers.data?.results.find((user) => user.email === email);
    const ticket = zendeskTickets.data?.find((ticket) => ticket.requester.email === email);

    return user && <TicketBubble userId={user.id} ticket={ticket} d2dNo={bookingInquiry.d2d_no} />;
  };

  const renderScrollButton = () => (
    <span
      className={clsx(classes.textPrimary, classes.baseButton)}
      onClick={() => onScrollToManagementTool()}
    >
      {strings.toManagementTool}
    </span>
  );

  return (
    <>
      {notAuthorizedAlert.renderDialog()}
      {renderCancelBookingDialog()}
      {renderChangeSalesperson()}
      <BookingInquiryOwnerChooseDialog
        open={openOwnerChooseDialog}
        onClose={() => setOpenOwnerChooseDialog(false)}
        setOwnerId={(id) => patchBookingInquiryOwner.mutateAsync(id)}
        setCompanyInfo={() => {}}
        setOwnerName={() => {}}
      />
      <SpinnerModal
        open={
          patchInquiryStatus.isLoading ||
          patchTrackingData.isLoading ||
          patchBookingInquiryOwner.isLoading ||
          postInquirySalespersonChina.isLoading ||
          postInquirySalespersonJapan.isLoading
        }
      />

      <div className={classes.managementInfoTable}>
        <div style={{ display: 'contents' }}>
          <div role="head">{strings.d2dNo}</div>
          <div role="head">{strings.createdAt}</div>
          <div role="head">{strings.applicant}</div>
          <div role="head">{strings.csManagerCN}</div>
          <div role="head">{strings.csManagerJP}</div>
          <div role="head">{strings.cancel}</div>
        </div>
        <div style={{ display: 'contents' }}>
          <div>{bookingInquiry.d2d_no}</div>
          <div>
            {bookingInquiry.created_at
              ? dayjs(bookingInquiry.created_at).format('YYYY/MM/DD HH:mm')
              : '-'}
          </div>
          <div>{`${bookingInquiry.user.last_name} ${bookingInquiry.user.first_name}`}</div>
          <div>{renderSalesperson('cn')}</div>
          <div>{renderSalesperson('jp')}</div>
          <div>{renderCancelCell()}</div>
        </div>
      </div>

      <VerticalSpace height={15} />

      <QueryContainer
        queries={[
          arrivalNotice,
          customsRequest,
          customsInstruction,
          deliveryInstruction,
          deliveryRequest,
          bookingStatusFlows,
        ]}
      >
        <div className={classes.bookingDetailsManagementInfoContainer}>
          <div role="head">{strings.basicInfo}</div>
          <div role="head">{strings.deliveryInfo}</div>
          <div role="head">{strings.customsInfo}</div>
          <div role="head">{strings.invoicePaymentInfo}</div>
          <div role="body" title="Job code">
            <span className={classes.managementInfoCellTitle}>{invoiceStrings.job_code}</span>
            {preAlert.job_code || '-'}
          </div>
          <div role="body" title="Sea delivery destination">
            <span className={classes.managementInfoCellTitle}>
              {arrivalNoticeStrings.sea_delivery_destination}
            </span>
            {arrivalNotice.data?.sea_delivery_destination_code ===
            arrivalNotice.data?.delivery_destination_code ? (
              deliveryDestinationOptions.find(
                (option) =>
                  option.delivery_destination_code ==
                  arrivalNotice.data?.sea_delivery_destination_code,
              )?.delivery_destination || '-'
            ) : arrivalNotice.data?.sea_delivery_destination_code ? (
              <span className={classes.textError}>{strings.inconsistentDeliveryDestination}</span>
            ) : (
              '-'
            )}
          </div>
          <div role="body" title="Customs agent">
            <span className={classes.managementInfoCellTitle}>
              {customsRequestStrings.customsAgent}
              <br />
              {bookingsStrings.salespersonCustoms}
            </span>
            <span>
              {customsRequest.data?.submission_target?.company || '-'}
              <br />
              {mapOptional(bookingInquiry.salesperson_customs, (s) => new UserHelper(s).fullname) ??
                '-'}
            </span>
          </div>
          <div role="body" title="Trade condition">
            <span className={classes.managementInfoCellTitle}>
              {bookingInquiryStrings.tradeCondition}
            </span>
            {bookingInquiry.trade_condition || '-'}
          </div>
          <div role="body" title="Sea job no">
            <span className={classes.managementInfoCellTitle}>{arrivalNoticeStrings.jobNo}</span>
            {/* Sea Job No. is synced as invoice_no in arrival-notice */}
            {arrivalNotice.data?.payment_info?.invoice_no || '-'}
          </div>
          <div role="body" title="Do status">
            <span className={classes.managementInfoCellTitle}>{preAlertStrings.doStatus}</span>
            {lastDoStatus?.name || '-'}
          </div>
          <div role="body" title="RCEP">
            <span className={classes.managementInfoCellTitle}>RCEP</span>
            {bookingInquiry.use_rcep ? formStrings.use : formStrings.notUse || '-'}
          </div>
          <div role="body" title="Pickup option">
            <span className={classes.managementInfoCellTitle}>
              {strings.pickup}
              <br />
              {strings.option}
            </span>
            {bookingInquiry.has_pickups
              ? bookingInquiryStrings.needsPickup
              : bookingInquiryStrings.noPickup || '-'}
          </div>
          <div role="body" title="Owner">
            <span className={classes.managementInfoCellTitle}>{strings.owner}</span>
            <span>
              <a
                className={clsx(classes.link, classes.textPrimaryImportant)}
                target="_blank"
                href={`${url('/admin/members/MemberDetails')}?id=${
                  bookingInquiry.owner_company?.id
                }`}
              >
                {bookingInquiry.owner_company?.english_name || '-'}
              </a>
              ({bookingInquiry.owner_company?.member_no})
              {`-${bookingInquiry.owner?.last_name} ${bookingInquiry.owner?.first_name}`}
              <span> </span>
              <span
                className={isAdmin ? classes.link : classes.linkDisabled}
                onClick={() => setOpenOwnerChooseDialog(true)}
              >
                {formStrings.change}
              </span>
              {bookingInquiry.owner && renderTicketBubble(bookingInquiry.owner?.email)}
            </span>
          </div>
          <div role="body" title="Move-out status">
            <span className={classes.managementInfoCellTitle}>{strings.moveOutStatus}</span>
            <span
              className={clsx(lastMoveOutStatus?.name === '搬入先が不一致' && classes.textError)}
            >
              {lastMoveOutStatus?.name || '-'}
            </span>
          </div>
          <div role="body" title="海上保険">
            <span className={classes.managementInfoCellTitle}>
              {instructionStrings.seaInsurance}
            </span>
            {customsInstruction.data?.insurance?.tag
              ? instructionStrings.insuranceTags[customsInstruction.data?.insurance?.tag]
              : '-'}
          </div>
          {/* <div role="body" title="Customs check">
            <span className={classes.managementInfoCellTitle}>
              {cargoTrackingStrings.customsCheck}
            </span>
            {hasCustomsCheck ? formStrings.exist : formStrings.none}
          </div> */}
          <div role="body" title="Servie name">
            <span className={classes.managementInfoCellTitle}>{routePlanStrings.serviceName}</span>
            {bookingInquiry.route_plan?.service_name
              ? routePlanServiceNameStrings[bookingInquiry.route_plan?.service_name]
              : '-'}
          </div>
          <div role="body" title="Consignee">
            <span className={classes.managementInfoCellTitle}>{bookingsStrings.consignee}</span>
            <span>
              {bookingInquiry.consignee?.user ? (
                <>
                  <a
                    className={clsx(classes.link, classes.textPrimaryImportant)}
                    target="_blank"
                    href={`/admin/members/MemberDetails?id=${consigneeCompany.data?.id}`}
                  >
                    {consigneeCompany.data?.english_name || '-'}
                  </a>{' '}
                  ({consigneeCompany.data?.member_no})
                </>
              ) : (
                bookingInquiry.consignee?.english_name || '-'
              )}
            </span>
          </div>
          <div role="body" title="Pick up date">
            <span className={classes.managementInfoCellTitle}>{strings.moveOutDate}</span>
            {formatDate(deliveryRequest.data?.pickup_date) || '-'}
          </div>
          <div role="body" title="Customs check">
            <span className={classes.managementInfoCellTitle}>
              {cargoTrackingStrings.customsCheck}
            </span>
            {hasCustomsCheck ? formStrings.exist : formStrings.none}
          </div>
          <div role="body" title="Route plan">
            <span className={classes.managementInfoCellTitle}>{strings.routePlanName}</span>
            {bookingInquiry.route_plan?.name || '-'}
          </div>
          <div role="body" title="POL/POD">
            <span className={classes.managementInfoCellTitle}>{strings.polPod}</span>
            <div>
              {preAlert?.pol || '-'}
              {'/'}
              {preAlert?.pod || '-'}
            </div>
          </div>
          <div role="body" title="Delivery date">
            <span className={classes.managementInfoCellTitle}>
              {deliveryRequestStrings.deliveryDate}
            </span>
            {formatDate(deliveryRequest.data?.delivery_date) || '-'}
          </div>
          <div role="body" title="Customs status">
            <span className={classes.managementInfoCellTitle}>{customsRequestStrings.status}</span>
            {lastCustomsStatus?.name || '-'}
          </div>
          <div role="body" title="Invoice">
            {isJapanStaff && (
              <>
                <span className={classes.managementInfoCellTitle}>{invoiceStrings.invoice}</span>
                <span>
                  {lastInvoiceStatus?.name || '-'}
                  <br />
                  {renderScrollButton()}
                </span>
              </>
            )}
          </div>
          <div role="body" title="Vessel">
            <span className={classes.managementInfoCellTitle}>{strings.vessel}</span>
            {preAlert.vessel || '-'}　{preAlert.voyage || '-'}
          </div>
          <div role="body" title="Receiver">
            <span className={classes.managementInfoCellTitle}>
              {deliveryRequestStrings.receiver}
            </span>
            {deliveryInstruction.data?.is_receiver_checked
              ? bookingInquiry.receiver?.company_name
              : deliveryInstruction.data?.receiver?.company_name || '-'}
          </div>
          <div role="body" title="Customs special note">
            <span className={classes.managementInfoCellTitle}>
              {bookingInquiryStrings.specialNote}
              <br />({strings.customs})
            </span>
            {customsInstruction.data?.has_special_note
              ? customsInstruction.data?.special_note
                  ?.filter((item) => item.selected)
                  .map((item) => (item.note ? `${item.title}:${item.note}` : item.title))
                  .join('; ') || '-'
              : formStrings.none}
          </div>
          <div role="body" title="Payment slip">
            {isJapanStaff && (
              <>
                <span className={classes.managementInfoCellTitle}>
                  {paymentSlipStrings.payment_slip}
                </span>
                <span>
                  {lastPaymentSlipStatus?.name || '-'}
                  <br />
                  {renderScrollButton()}
                </span>
              </>
            )}
          </div>
          <div role="body" title="ETD-ETA">
            <span className={classes.managementInfoCellTitle}>
              {strings.etd}-{strings.eta}
            </span>
            {preAlert.is_empty
              ? '-'
              : `${formatDate(preAlert.etd, 'short') || ''}-${
                  formatDate(preAlert.eta, 'short') || ''
                }`}
          </div>
          <div role="body" title="Receiver address 1">
            <span className={classes.managementInfoCellTitle}>
              {deliveryRequestStrings.receiver}
              {contactStrings.address1}
            </span>
            <span className={classes.noWrapCell}>
              {deliveryInstruction.data?.is_receiver_checked
                ? bookingInquiry.receiver?.address1
                : deliveryInstruction.data?.receiver?.address1 || '-'}
            </span>
          </div>
          <div role="body" title="Food application">
            <span className={classes.managementInfoCellTitle}>
              {strings.foodDelivery}・
              <br />
              {strings.applicationRecord}
            </span>
            {new AdminInquiryDetailHelper(bookingInquiry).getFirstFoodApplicationStatus(preAlert)}
          </div>

          <div role="body" title="Sales">
            {isJapanStaff && (
              <>
                <span className={classes.managementInfoCellTitle}>
                  {bookingInquiryProfitStrings.sales}
                </span>
                {sales ? moneyStrings.formatYenFront(sales) : '-'}
              </>
            )}
          </div>
          <div role="body" title="ATD-ATA">
            <span className={classes.managementInfoCellTitle}>
              {vesselScheduleStrings.atd}-{vesselScheduleStrings.ata}
            </span>
            {preAlert.is_empty
              ? '-'
              : `${formatDate(preAlert.atd, 'short') || ''}-${
                  formatDate(preAlert.ata, 'short') || ''
                }`}
          </div>
          <div role="body" title="Truck company">
            <span className={classes.managementInfoCellTitle}>
              {deliveryRequestStrings.truckCompany}
            </span>
            {deliveryRequest.data?.partner?.name_ja || '-'}
          </div>
          <div />
          <div role="body" title="Gross profit">
            {isJapanStaff && (
              <>
                <span className={classes.managementInfoCellTitle}>
                  {bookingInquiryProfitStrings.grossProfit}
                </span>
                {grossProfit ? moneyStrings.formatYenFront(grossProfit) : '-'}
              </>
            )}
          </div>
          <div role="body" title="Commodity">
            <span className={classes.managementInfoCellTitle}>{preAlertStrings.commodity}</span>
            {preAlert.commodity || '-'}
          </div>
          <div role="body" title="Truck type">
            <span className={classes.managementInfoCellTitle}>
              {deliveryRequestStrings.truckTypeAndCount}
            </span>
            {deliveryRequest.data?.truck_type
              ? `${deliveryRequest.data?.truck_type} x ${deliveryRequest.data?.truck_count || '-'}`
              : '-'}
          </div>
          <div role="body" />
          <div role="body" />
          <div role="body" title="Container No.">
            <span className={classes.managementInfoCellTitle}>
              {customsRequestStrings.container_no}
            </span>
            {customsRequest.data?.container_no || '-'}
          </div>
          <div role="body" title="Delivery status">
            <span className={classes.managementInfoCellTitle}>{strings.deliveryStatus}</span>
            {lastDeliveryStatus?.name || '-'}
          </div>
          <div role="body" />
          <div role="body" />
          <div role="body" title="Commodity info">
            <span className={classes.managementInfoCellTitle}>{strings.commodityInfo}</span>
            {preAlert.quantity || '-'}CNTS/
            {parseFloat(preAlert.gross_weight)}KGS/
            {parseFloat(preAlert.measurement)}M3
          </div>
          <div role="body" title="Truck special note">
            <span className={classes.managementInfoCellTitle}>
              {bookingInquiryStrings.specialNote}
              <br />({strings.truck})
            </span>
            <span className={classes.noWrapCell}>
              {deliveryInstruction.data?.has_special_note
                ? deliveryInstruction.data?.special_note || '-'
                : formStrings.none}
            </span>
          </div>
          <div role="body" />
          <div role="body" />
          <div role="body" title="Price grade">
            <span className={classes.managementInfoCellTitle}>
              {strings.price}
              <br />
              {strings.grade}
            </span>
            {bookingInquiry.route_plan_simulator_result?.grade
              ? allCompanyLevel[bookingInquiry.route_plan_simulator_result?.grade]
              : '-'}
          </div>
          <div role="body" />
          <div role="body" />
          <div role="body" />
        </div>
      </QueryContainer>
    </>
  );
};
